import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart, DonutChart, Legend } from "@tremor/react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Wallet, TrendingUp, Loader2, Plus, Trash2 } from "lucide-react";
import { api, formatMXN } from "@/lib/api";
import { toast } from "sonner";
import MovementDialog from "@/components/MovementDialog";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";

const DONUT_COLORS = ["rose", "amber", "sky", "violet", "emerald", "blue", "slate"];

const StatCard = ({ title, micro, value, icon: Icon, accent, border, bg, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className={`rounded-2xl border p-5 bg-card ${border}`}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${bg}`}>
        <Icon className={`w-[18px] h-[18px] ${accent}`} strokeWidth={2.2} />
      </div>
    </div>
    <p className={`font-numeric font-bold text-2xl sm:text-3xl mt-4 tracking-tight ${accent}`}>{value}</p>
    <p className="text-xs text-muted-foreground mt-1">{micro}</p>
  </motion.div>
);

export default function Dashboard() {
  const { currency, format: fmt, compact } = useCurrency();
  const { user } = useAuth();
  const canWrite = user?.role !== "viewer";
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
  });
  const { data: accounts = [] } = useQuery({ queryKey: ["accounts"], queryFn: async () => (await api.get("/accounts")).data });
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: async () => (await api.get("/vaults/members")).data });
  const isShared = members.filter((m) => m.status !== "pending").length > 1;
  const accName = (id) => accounts.find((a) => a.id === id)?.name;
  const authorLine = (m) => (!isShared ? "" : m.updated_by_name ? ` · editado por ${m.updated_by_name}` : m.created_by_name ? ` · por ${m.created_by_name}` : "");

  const delMovement = async (m) => {
    if (!window.confirm(`¿Eliminar este movimiento de ${m.category}?`)) return;
    try { await api.delete(`/movements/${m.id}`); toast.success("Movimiento eliminado"); qc.invalidateQueries(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  if (isLoading || !data) {
    return (
      <div className="h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8 py-4" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Bóveda principal</h1>
          <p className="text-muted-foreground text-sm mt-1">Tu balance consolidado y el pulso entre By y To.</p>
        </div>
      </div>

      {/* Balance hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl border border-border bg-gradient-to-br from-card to-secondary/40 p-6 sm:p-8"
        data-testid="balance-card"
      >
        <div className="flex items-center gap-2 text-muted-foreground">
          <Wallet className="w-4 h-4" />
          <span className="text-xs font-semibold uppercase tracking-wider">Balance total consolidado</span>
        </div>
        <p
          data-testid="balance-value"
          className={`font-numeric font-bold text-4xl sm:text-5xl lg:text-6xl mt-3 tracking-tight ${
            data.balance >= 0 ? "text-foreground" : "text-rose-500"
          }`}
        >
          {fmt(data.balance)}
        </p>
        {currency !== "MXN" && (
          <p className="text-sm text-muted-foreground mt-2 font-numeric">≈ {formatMXN(data.balance)} MXN</p>
        )}
      </motion.div>

      <div className="grid sm:grid-cols-2 gap-4">
        <StatCard title="By · Origen de los fondos" micro="Total histórico registrado" value={fmt(data.total_by)}
          icon={ArrowUpRight} accent="text-emerald-500" border="border-emerald-500/20" bg="bg-emerald-500/10" delay={0.05} />
        <StatCard title="To · Destino del dinero" micro="Total histórico registrado" value={fmt(data.total_to)}
          icon={ArrowDownRight} accent="text-rose-500" border="border-rose-500/20" bg="bg-rose-500/10" delay={0.1} />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-emerald-500" />
            <h3 className="text-lg font-semibold">Comparativa mensual</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">By vs To · últimos 6 meses</p>
          <BarChart
            data={data.monthly}
            index="month"
            categories={["By", "To"]}
            colors={["emerald", "rose"]}
            valueFormatter={(v) => compact(v)}
            showLegend={true}
            yAxisWidth={56}
            className="h-72"
            data-testid="monthly-bar-chart"
          />
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5">
          <h3 className="text-lg font-semibold mb-1">Destino del mes</h3>
          <p className="text-xs text-muted-foreground mb-4">Distribución de tus «To»</p>
          {data.categories.length > 0 ? (
            <>
              <DonutChart
                data={data.categories}
                category="value"
                index="name"
                colors={DONUT_COLORS}
                valueFormatter={(v) => fmt(v)}
                className="h-52"
                data-testid="category-donut-chart"
              />
              <Legend
                categories={data.categories.slice(0, 6).map((c) => c.name)}
                colors={DONUT_COLORS}
                className="mt-4 justify-center"
              />
            </>
          ) : (
            <div className="h-52 flex items-center justify-center text-sm text-muted-foreground text-center px-4">
              Aún no hay «To» este mes. Registra un movimiento para ver la distribución.
            </div>
          )}
        </div>
      </div>

      {/* Spend by account */}
      {data.by_account && data.by_account.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="text-lg font-semibold mb-1">Gasto por cuenta</h3>
          <p className="text-xs text-muted-foreground mb-4">¿De dónde se va tu dinero este mes?</p>
          <BarChart
            data={data.by_account}
            index="name"
            categories={["Gasto"]}
            colors={["rose"]}
            layout="vertical"
            valueFormatter={(v) => fmt(v)}
            showLegend={false}
            yAxisWidth={120}
            className="h-64"
            data-testid="by-account-chart"
          />
        </div>
      )}

      {/* Recent */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Movimientos recientes</h3>
          <Link to="/movimientos" data-testid="view-all-movements" className="text-sm font-medium text-emerald-500 hover:underline">Ver todos</Link>
        </div>
        {data.recent.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Sin movimientos todavía.</p>
        ) : (
          <div className="divide-y divide-border" data-testid="recent-movements">
            {data.recent.map((m) => {
              const isBy = m.type === "by";
              return (
                <div key={m.id} data-testid={`recent-item-${m.id}`}
                  className="group w-full flex items-center gap-3 py-3 hover:bg-accent/40 rounded-lg px-2 -mx-2 transition-colors">
                  <button onClick={() => canWrite && setDialog({ open: true, initial: m })} disabled={!canWrite} className="flex items-center gap-3 flex-1 min-w-0 text-left disabled:cursor-default">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isBy ? "bg-emerald-500/10" : "bg-rose-500/10"}`}>
                      {isBy ? <ArrowUpRight className="w-4 h-4 text-emerald-500" /> : <ArrowDownRight className="w-4 h-4 text-rose-500" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{m.category}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {m.movement_date}{accName(m.account_id) ? ` · ${accName(m.account_id)}` : ""}{m.responsible ? ` · ${m.responsible}` : ""}{authorLine(m)}
                      </p>
                    </div>
                  </button>
                  <span className={`font-numeric font-semibold text-sm ${isBy ? "text-emerald-500" : "text-rose-500"}`}>
                    {isBy ? "+" : "−"}{fmt(m.amount)}
                  </span>
                  {canWrite && (
                    <button data-testid={`delete-recent-${m.id}`} onClick={() => delMovement(m)} title="Eliminar"
                      className="w-8 h-8 rounded-lg hover:bg-rose-500/10 flex items-center justify-center text-muted-foreground hover:text-rose-500 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button data-testid="fab-quick-register" onClick={() => setDialog({ open: true, initial: null })}
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xl shadow-emerald-900/40 flex items-center justify-center transition-transform hover:scale-105 active:scale-95 ${canWrite ? "" : "hidden"}`}>
        <Plus className="w-7 h-7" strokeWidth={2.5} />
      </button>

      <MovementDialog open={dialog.open} onOpenChange={(o) => setDialog({ open: o, initial: o ? dialog.initial : null })} initial={dialog.initial} />
    </div>
  );
}
